import React from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import MainLogo from "../../assets/images/AstraLogo.svg";
import { forgotPassword } from "../../shared/_services/auth.services";
import { setChallengeToken } from "../../shared/_helpers/otpChallenge";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const handleAuthentication = async ({ email }) => {
    let updatedemail = email.toLowerCase();
    const data = await forgotPassword({ email: updatedemail });
    if (data.status === 200) {
      // Backend SEC-114: /generate-otp returns the challenge token that
      // /otp-verify requires to redeem the code it just sent. Without it the
      // verify screen has no credential and every correct code gets a 401.
      // The purpose rides with the credential rather than with the
      // `verifyCodeFrom` breadcrumb: this is the only flow allowed to mint a
      // password-reset token, and a breadcrumb outlives the flow that set it.
      setChallengeToken(data?.data?.challengeToken, { purpose: 'reset_password' });
      window.localStorage.setItem('resetPasswordEmail', email);
      enqueueSnackbar(data?.data.message, {
        variant: 'success',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });

      sessionStorage.setItem('verifyCodeFrom', 'forgotpassword');
      navigate('/verify-code', { state: { from: 'forgotpassword' } });
    }
    else {
      enqueueSnackbar(data?.response?.data?.error || data?.response?.data?.message, {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
    }
  };

  const initialValues = {
    email: "",
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required("Email is required")
      .test(
        "contains-at-and-dot",
        "Email must contain '@' and '.'",
        (value) => {
          if (!value) return false; // Required validation
          return value.includes("@") && value.includes(".");
        }
      )
      .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Invalid email address"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      await handleAuthentication(values);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-signup-wrap forget-wrap">
      <div className="container">
        <div className="row">
          <div className="col-md-12 col-lg-6 logo-wrap place-content-center">
            <Link to="/">
              <img
                className="login-signup-logo"
                src={MainLogo}
                alt="Astra Logo"
              />
            </Link>
            <div className="auth-page-content">
              <h2>
                Forgot Your Password?
              </h2>
              <p>
                No worries. Enter your registered phone number and we’ll send you a verification code to reset your password and regain access to your financial dashboard.
              </p>
              <p className="w-100">
                <svg width="46" height="3" viewBox="0 0 46 3" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 1.5H46" stroke="white" strokeWidth="2" />
                </svg>
                &nbsp;&nbsp;&nbsp;
                PROFESSIONAL FINANCIAL ADVISORY</p>
            </div>
          </div>
          <div className="col-md-12 col-lg-6 place-content-center">
            <div className="login-signup-box signup-box">
             <h2 className="login-signup-head text-left mt-3 mb-5">Forgot Password</h2>
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ errors, touched, isSubmitting }) => (
                  <Form>
                    <div className="form-group">
                      <Field
                        type="email"
                        id="email"
                        name="email"
                        placeholder="Email address"
                        className={`form-control ${errors.email && touched.email ? "is-invalid" : ""
                          }`}
                      />
                      <ErrorMessage
                        name="email"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <p className="f-500 text-light" style={{ fontSize: "14px" }}>
                      Enter your email and we’ll send you link to reset your password.
                    </p>
                    <button
                      type="submit"
                      className="auth-screen-btn mt-4 mb-2"
                      disabled={isSubmitting}
                    >
                       {isSubmitting ? "Sending..." : "Send Verification Code"}
                    </button>
                    <p className="text-center text-secondary mt-3 mobile-hidden-text f-700">
                      <svg width="18" height="14" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M16.5 7L2.25 7" stroke="#ffffff" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" />
                        <path d="M7.5 1L1.5 7L7.5 13" stroke="#ffffff" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" />
                      </svg>
                      &nbsp;&nbsp;
                      <Link className="text-light f-400" to="/login">
                        Back to login
                      </Link>
                    </p>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
